import { LogManager } from '@src/utility/Logger';
import { toJS } from 'mobx';

import { extractErrorInfo } from '../utility/error';
import { saveVideoResources } from './VideoResourcesProvider';

import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { CurrentLoadedVideo } from '@src/stores/VideoCreatorStore';
import type { PreparedVideoSave, StoryDTO } from '@src/types.ts/story';

/**
 * Purpose: Save the current video snapshot asynchronously without blocking
 * UI workflows (e.g., aspect ratio switching). This avoids awaiting the
 * heavy network/serialization path and prevents setting `isSaving` on the
 * store, which currently gates other operations (like loading a different
 * aspect ratio variant via `loadVideo`).
 */
export class BackgroundVideoSaveService {
  private logger = LogManager.getLogger('service:BackgroundVideoSave');
  private preparing = new WeakSet<IVideoCreatorStore>();
  private saving = new WeakSet<IVideoCreatorStore>();

  public async prepare(
    store: IVideoCreatorStore,
    withRenderer = true,
    autoSave = false,
  ): Promise<PreparedVideoSave | null> {
    if (this.preparing.has(store)) {
      this.logger.debug('prepare:skip:already-in-flight');
      return null;
    }
    if (
      !store.currentVideo ||
      !store.story ||
      !store.datoClientStore?.videoRepository
    ) {
      this.logger.debug('prepare:skip:missing-deps');
      return null;
    }
    this.preparing.add(store);

    const videoSnapshot: CurrentLoadedVideo = toJS(store.currentVideo);

    await store.prepareCurrentVideoForSave(withRenderer, videoSnapshot);
    store.addVideoAction(
      {
        editor: store.rootStore.userIdentityStore.currentEditor,
        date: new Date().getTime(),
        type: autoSave ? 'autosave' : 'save',
      },
      videoSnapshot,
    );

    const storySnapshot: StoryDTO =
      await store.punchListManager.attachPunchListPhotosToStory();

    const migrationLog =
      store.rootStore.editorStore.transcriptSegmentArchitectureMigrationLog;
    const subtitlesLines = store.subtitlesProcessor.getSubtitleLines();

    this.preparing.delete(store);

    return {
      videoSnapshot,
      storySnapshot,
      migrationLogStatus: migrationLog?.status,
      migrationLogDebug: migrationLog?.debugInfo,
      subtitlesLines,
    };
  }

  public async save(
    prepared: PreparedVideoSave | null,
    store: IVideoCreatorStore,
  ): Promise<void> {
    if (!prepared) return;
    if (this.saving.has(store)) {
      this.logger.debug('save:skip:already-in-flight');
      return;
    }
    this.saving.add(store);
    try {
      const {
        videoSnapshot,
        storySnapshot,
        subtitlesLines,
        migrationLogStatus,
        migrationLogDebug,
      } = prepared;

      if (store.savingError?.isBackground) {
        store.savingError = undefined;
      }

      const resourcesMetadata = await saveVideoResources(
        storySnapshot.id,
        {
          videoSourceSnapshot: videoSnapshot.videoSource,
          ...(store.getFlag('enableTranscriptSnapshotResourceUpload') && {
            transcriptionSnapshot: videoSnapshot.transcriptionSnapshot || [],
          }),
          ...(videoSnapshot.karaokeData && {
            karaokeData: videoSnapshot.karaokeData,
          }),
        },
        {
          useS3: store.getFlag('enableVideoResourcesS3Upload') ?? true,
          dualWrite: store.getFlag('enableVideoResourcesS3DualWrite') ?? false,
        },
      );

      if (
        !resourcesMetadata ||
        Object.entries(resourcesMetadata).some(([, v]) => !v)
      ) {
        this.logger.error('save:resources-metadata-invalid', {
          resourcesMetadata,
        });
        return;
      }
      if (
        store.getFlag('enableTranscriptSnapshotResourceUpload') &&
        !resourcesMetadata.transcriptionSnapshot
      ) {
        this.logger.error('save:resources-metadata-transcript-snapshot-fail');
        return;
      }
      if (!resourcesMetadata.videoSourceSnapshot) {
        this.logger.error('save:video-source-snapshot-missing');
        return;
      }

      const payload = {
        ...videoSnapshot,
        resourcesMetadata: {
          videoSourceSnapshot: resourcesMetadata.videoSourceSnapshot,
          ...(resourcesMetadata.transcriptionSnapshot && {
            transcriptionSnapshot: resourcesMetadata.transcriptionSnapshot,
          }),
          ...(resourcesMetadata.karaokeData && {
            karaokeData: resourcesMetadata.karaokeData,
          }),
        },
        ...((!resourcesMetadata.transcriptionSnapshot ||
          store.getFlag('enableTranscriptSnapshotResourceDualWrite')) && {
          transcriptionSnapshot: videoSnapshot.transcriptionSnapshot,
        }),
        ...(resourcesMetadata.transcriptionSnapshot &&
          !store.getFlag('enableTranscriptSnapshotResourceDualWrite') && {
            transcriptionChanges: [],
          }),
        transcriptionMigrationStatus: migrationLogStatus,
        transcriptionMigrationLog: migrationLogDebug,
        subtitles: subtitlesLines,
      };
      const videoResult =
        await store.datoClientStore.videoRepository.saveOrUpdateVideo(payload);
      this.logger.debug('save:video-success', { id: videoResult.id });

      videoSnapshot.slug = videoResult.slug;
      videoSnapshot.hash = videoResult.hash;
      videoSnapshot._publishedAt = new Date().toISOString();

      if (!videoSnapshot.id) {
        videoSnapshot.id = videoResult.id;
        if (videoSnapshot.parentVideo) {
          await store.addCurrentToParentVideos(videoSnapshot);
        } else {
          await store.addCurrentToStoryVideos(videoSnapshot);
        }
      }

      const storyResult = await store.updateStory(storySnapshot);
      this.logger.debug('save:story-success', { id: storyResult.id });
    } catch (e) {
      this.logger.warn('save:failed', extractErrorInfo(e));
      store.savingError = e as IVideoCreatorStore['savingError'];
      if (store.savingError) {
        store.savingError.isBackground = true;
        store.savingError.preparedVideoSave = prepared;
      }
    } finally {
      this.saving.delete(store);
    }
  }
}

// Singleton export for convenience (lightweight service)
export const backgroundVideoSaveService = new BackgroundVideoSaveService();
